import { Injectable } from '@angular/core';
import { NgbDateStruct, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { addBusinessDays, addDays, addYears, isValid } from 'date-fns';
import addMonths from 'date-fns/addMonths';
import { CustomDatePipe } from 'src/app/core/pipes/custom-date.pipe';
import { AuthService } from 'src/app/core/services/auth.service';
import { ALT_DATETIME_FORMAT, ISO8601_DATE_FORMAT } from '../constants/date';
import { convertToLocalTime } from '../utils/date-util';
import { DatetimeStruct } from '../utils/datetime-struct';

@Injectable({
  providedIn: 'root'
})
export class DateService {
  constructor(
    private authService: AuthService,
    private datePipe: CustomDatePipe
  ) {}

  /**
   * Combines a `NgbDateStruct` and `NgbTimeStruct` into a `Date`.
   *
   * @param datePart The `NgbDateStruct` from which to extract year, month, and day
   * @param timePart The `NgbTimeStruct` from which to extract hours, minutes, and seconds
   * @param timezone
   */
  getDatetime(datePart: NgbDateStruct, timePart: NgbTimeStruct, timezone?: string): Date {
    if (datePart != null || timePart != null) {
      return this.getTimezoneAwareDate(
        new Date(
          datePart && datePart.year,
          datePart && datePart.month - 1,
          datePart && datePart.day,
          (timePart && timePart.hour) || 0,
          (timePart && timePart.minute) || 0,
          (timePart && timePart.second) || 0
        ),
        timezone
      );
    }

    return null;
  }

  /**
   * Constructs a date using the specified offsets.
   *
   * @param endOfYear   `true` if the month and day should be set to December 31st
   *                    or `false` if they should match today's month and day.
   * @param yearOffset  Number of years to add to today's date
   * @param monthOffset Number of months to add to today's date
   * @param dayOffset   Number of days to add to today's date
   * @param timezone    Timezone to use for the date
   */
  getDateWithOffset(endOfYear = true, yearOffset = 3, monthOffset = 0, dayOffset = 0, timezone: string = null): Date {
    const now = new Date();

    return this.getTimezoneAwareDate(
      new Date(
        now.getFullYear() + yearOffset,
        endOfYear ? 11 : now.getMonth() + monthOffset, // 0 is January, 11 is December
        endOfYear ? 31 : now.getDate() + dayOffset
      ),
      timezone
    );
  }

  getDateFromString(dateString: string, timezone?: string): Date {
    const dateParts = dateString?.split('-');

    if (dateParts?.length === 3) {
      return this.getTimezoneAwareDate(
        new Date(Number(dateParts[2]), Number(dateParts[0]) - 1, Number(dateParts[1])),
        timezone
      );
    } else {
      throw Error(`Date should be in MM-DD-YYYY format. Received date: ${dateString}`);
    }
  }

  getAbsoluteMaxDate(includeTime = true, timezone?: string): Date {
    return this.getTimezoneAwareDate(
      new Date(2099, 11, 31, includeTime ? 23 : 0, includeTime ? 59 : 0, includeTime ? 59 : 0),
      timezone
    );
  }

  getAbsoluteMinDate(timezone?: string): Date {
    return this.getTimezoneAwareDate(new Date(1901, 0, 1), timezone);
  }

  getDatetimeStruct(date: Date | number | string, timezone?: string, includeTime = true): DatetimeStruct {
    date = date == null ? null : new Date(date);

    if (date != null && isValid(date)) {
      const dateFormatted = this.datePipe.transform(date, ALT_DATETIME_FORMAT, timezone);

      const datetimeParts = dateFormatted.split(' ');
      const dateParts = datetimeParts[0].split('-');
      const timeParts = includeTime ? datetimeParts[1].split(':') : null;

      return new DatetimeStruct({
        year: Number(dateParts[0]),
        month: Number(dateParts[1]),
        day: Number(dateParts[2]),
        hour: timeParts && Number(timeParts[0]),
        minute: timeParts && Number(timeParts[1]),
        second: timeParts && Number(timeParts[2])
      });
    }

    return null;
  }

  /**
   * A timezone-aware `now()` function.
   */
  now(includeTime = true, timezone?: string): Date {
    const now = new Date();

    return this.getTimezoneAwareDate(
      new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        includeTime ? now.getHours() : 0,
        includeTime ? now.getMinutes() : 0,
        includeTime ? now.getSeconds() : 0,
        includeTime ? now.getMilliseconds() : 0
      ),
      timezone
    );
  }

  /**
   * Constructs a midnight UTC date using the day/month/year values
   * from the provided date in the user's current timezone.
   */
  getMidnightUTCDate(date: Date | number | string): Date {
    return this.getDatetime(this.getDatetimeStruct(date), null, 'UTC');
  }

  getBrowserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  /**
   * Safely adds days to one of our midnight UTC dates.
   * This is necessary to account for when a daylight saving time transition
   * happens in the start/end date range.
   */
  addDaysUTC(date: Date | number | string, days: number): Date {
    return this.getMidnightUTCDate(addDays(this.getMidnightSystemDateFromMidnightUTCDate(date), days));
  }

  /**
   * Safely subtracts days from one of our midnight UTC dates.
   * This is necessary to account for when a daylight saving time transition
   * happens in the start/end date range.
   */
  subDaysUTC(date: Date | number | string, days: number): Date {
    return this.addDaysUTC(date, -days);
  }

  /**
   * Safely adds months to one of our midnight UTC dates.
   * This is necessary to account for when a daylight saving time transition
   * happens in the start/end date range.
   */
  addMonthsUTC(date: Date | number | string, months: number): Date {
    return this.getMidnightUTCDate(addMonths(this.getMidnightSystemDateFromMidnightUTCDate(date), months));
  }

  /**
   * Safely subtracts months from one of our midnight UTC dates.
   * This is necessary to account for when a daylight saving time transition
   * happens in the start/end date range.
   */
  subMonthsUTC(date: Date | number | string, months: number): Date {
    return this.addMonthsUTC(date, -months);
  }

  /**
   * Safely adds years to one of our midnight UTC dates.
   * This is necessary to account for when a daylight saving time transition
   * happens in the start/end date range.
   */
  addYearsUTC(date: Date | number | string, years: number): Date {
    return this.getMidnightUTCDate(addYears(this.getMidnightSystemDateFromMidnightUTCDate(date), years));
  }

  /**
   * Safely subtracts years from one of our midnight UTC dates.
   * This is necessary to account for when a daylight saving time transition
   * happens in the start/end date range.
   */
  subYearsUTC(date: Date | number | string, years: number): Date {
    return this.addYearsUTC(date, -years);
  }

  /**
   * Safely adds business days to one of our midnight UTC dates.
   * This is necessary to account for when a daylight saving time transition
   * or weekend day happens in the start/end date range.
   */
  addBusinessDaysUTC(date: Date | number | string, days: number): Date {
    return this.getMidnightUTCDate(addBusinessDays(this.getMidnightSystemDateFromMidnightUTCDate(date), days));
  }

  /**
   * Safely subtracts business days from one of our midnight UTC dates.
   * This is necessary to account for when a daylight saving time transition
   * or weekend day happens in the start/end date range.
   */
  subBusinessDaysUTC(date: Date | number | string, days: number) {
    return this.addBusinessDaysUTC(date, -days);
  }

  getMidnightSystemDateFromMidnightUTCDate(date: Date | number | string): Date {
    const systemDateString = this.datePipe.transform(date, ISO8601_DATE_FORMAT, 'UTC');

    return systemDateString ? new Date(systemDateString + 'T00:00:00') : null;
  }

  private getTimezoneAwareDate(date: Date, timezone?: string): Date {
    timezone = timezone || (this.authService.timezone && this.authService.fullFormTimezone);

    // console.log('getTimezoneAwareDate', date, timezone);

    if (timezone) {
      return convertToLocalTime(date, timezone);
    } else {
      return date;
    }
  }
}
